import React, {useCallback, useEffect, useRef, useState} from 'react';
import {css} from 'emotion';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import Popover from '@amzn/meridian/popover';
import Column from '@amzn/meridian/column';
import caretDownTokens from '@amzn/meridian-tokens/base/icon/caret-down';
import {useHistory, useLocation} from 'react-router-dom';
import {useAppSelector} from '../../../app/hooks';
import {selectCachedBusiness, selectCachedRegion} from '../../cached-state/cachedStateSlice';
import {getConstraintsList} from '../../../common/apis/BackendApisClient';
import {ConstraintRecord} from '../../../common/apis/models/getConstraintsListResponse';
import {buildConstraintPath} from '../../../common/utils/constraintRoutes';

const detailButtonStyle = css`
  background-color: transparent !important;
  border: none;
  cursor: pointer;
  padding: 0 !important;
  box-shadow: none !important;
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const mastheadTextStyle = css`
  margin-right: 2px !important;
  margin-left: 2px !important;
  color: black !important;
`;

const constraintItemStyle = css`
  cursor: pointer;
  padding: 8px 12px;
  background: none;
  border: none;
  text-align: left;
  width: 100%;
  display: block;
  font: inherit;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const popoverContentStyle = css`
  max-height: 300px;
  overflow-y: auto;
  min-width: 220px;
`;

const ConstraintDropdown = () => {
  const {pathname} = useLocation();
  const history = useHistory();
  const businessType = useAppSelector(selectCachedBusiness);
  const region = useAppSelector(selectCachedRegion);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [constraints, setConstraints] = useState<ConstraintRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const onClickButton = useCallback(() => {
    // Small delay to prevent immediate close from event propagation on fresh load
    setTimeout(() => setOpen(true), 0);
  }, []);
  const onClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (businessType && region) {
      setLoading(true);
      getConstraintsList({businessType, region, query: ''})
        .then((response) => {
          if (response) {
            setConstraints(response.records);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [businessType, region]);

  const handleConstraintClick = (constraint: ConstraintRecord) => {
    setOpen(false);
    history.push(buildConstraintPath(constraint.constraintName, constraint.constraintId));
  };

  if (pathname === '/select-business') {
    return null;
  }

  // Determine the label: show current constraint name if on a constraint page
  let dropdownLabel = 'Go to Constraint';
  if (pathname.startsWith('/constraints/')) {
    const currentConstraint = constraints.find((c) => pathname.endsWith(`-${c.constraintId}`));
    dropdownLabel = currentConstraint?.constraintName || dropdownLabel;
  }

  return (
    <>
      <button className={detailButtonStyle} onClick={onClickButton} ref={buttonRef} aria-expanded={open} aria-haspopup="true">
        <Text type="h200" transform="uppercase" className={mastheadTextStyle}>
          {dropdownLabel}
        </Text>
        <Icon tokens={caretDownTokens} className={mastheadTextStyle} />
      </button>
      <Popover
        anchorNode={buttonRef.current!}
        open={open && Boolean(buttonRef.current)}
        onClose={onClose}
        position="bottom"
        alignment="left"
      >
        <div className={popoverContentStyle}>
          <Column spacing="none">
            {loading && (
              <div className={constraintItemStyle}>
                <Text type="b200" color="secondary">
                  Loading...
                </Text>
              </div>
            )}
            {!loading &&
              constraints.map((constraint) => (
                <button
                  key={constraint.constraintId}
                  className={constraintItemStyle}
                  onClick={() => handleConstraintClick(constraint)}
                  type="button"
                >
                  <Text type="b200">{constraint.constraintName}</Text>
                  <Text type="b100" color="secondary">
                    {' '}
                    ({constraint.inputGranularity})
                  </Text>
                </button>
              ))}
            {!loading && constraints.length === 0 && (
              <div className={constraintItemStyle}>
                <Text type="b200" color="secondary">
                  No constraints found
                </Text>
              </div>
            )}
          </Column>
        </div>
      </Popover>
    </>
  );
};

export default ConstraintDropdown;
